import { useTranslation } from "react-i18next";
import type { CorpusSummary } from "../../../api/content/corporaApi";
import AppButton from "../../../ui/buttons/AppButton";
import Field from "../../../ui/forms/Field";
import OptionLabel from "../../../ui/forms/OptionLabel";
import { FORM_CONTROL_CLASS, FORM_SELECT_CLASS } from "../../../ui/styles/formStyles";
import {
  NEW_CORPUS,
  segmentationTemplateText,
  segmentationText,
  SEGMENTATION_TEMPLATES,
  type CorpusTargetDraft,
  type SegmentationTemplateName,
} from "./corpusImport";

const TEMPLATE_NAMES = Object.keys(SEGMENTATION_TEMPLATES) as SegmentationTemplateName[];

interface CorpusTargetFieldsProps {
  draft: CorpusTargetDraft;
  onChange: (draft: CorpusTargetDraft) => void;
  corpora: readonly CorpusSummary[];
  segmentationInvalid: boolean;
  /**
   * Only a JSON Lines import is cut by a segmentation given here, and may re-segment an
   * existing corpus. Imports through a source take the converter's segments, which follow
   * what the import Workflow reads (F16).
   */
  jsonLines: boolean;
}

/**
 * The corpus an import goes into: an existing one, or a new one by its name, and for JSON
 * Lines its segmentation. While the project has no corpus there is nothing to choose: the
 * name alone (F16).
 */
export default function CorpusTargetFields({ draft, onChange, corpora, segmentationInvalid, jsonLines }: CorpusTargetFieldsProps) {
  const { t } = useTranslation();
  const corpus = corpora.find((candidate) => candidate.corpusId === draft.corpusId) ?? null;
  return (
    <div className="space-y-3">
      {corpora.length === 0 ? null : (
        <Field label={t("sources.import.corpus")}>
          {(control) => (
            <select {...control} className={FORM_SELECT_CLASS} value={draft.corpusId}
              onChange={(event) => { onChange({ ...draft, corpusId: event.target.value, replaceSegmentation: false }); }}>
              {corpora.map((candidate) => <option key={candidate.corpusId} value={candidate.corpusId}>{candidate.name}</option>)}
              <option value={NEW_CORPUS}>{t("sources.import.newCorpus")}</option>
            </select>
          )}
        </Field>
      )}
      {corpus === null ? (
        <Field label={t(corpora.length === 0 ? "sources.import.corpusName" : "sources.import.newCorpusName")}>
          {(control) => <input {...control} className={FORM_CONTROL_CLASS} value={draft.newName} required onChange={(event) => { onChange({ ...draft, newName: event.target.value }); }} />}
        </Field>
      ) : jsonLines ? (
        <OptionLabel>
          <input type="checkbox" className="app-checkbox" checked={draft.replaceSegmentation}
            onChange={(event) => { onChange({ ...draft, replaceSegmentation: event.target.checked, segmentationText: event.target.checked ? segmentationText(corpus.segmentation) : draft.segmentationText }); }} />
          {t("sources.import.replaceSegmentation")}
        </OptionLabel>
      ) : null}
      {jsonLines && (corpus === null || draft.replaceSegmentation) ? (
        <SegmentationEditor text={draft.segmentationText} invalid={segmentationInvalid} onChange={(text) => { onChange({ ...draft, segmentationText: text }); }} />
      ) : null}
    </div>
  );
}

function SegmentationEditor({ text, invalid, onChange }: { text: string; invalid: boolean; onChange: (text: string) => void }) {
  const { t } = useTranslation();
  return (
    <Field
      label={t("sources.import.segmentation")}
      hint={invalid ? undefined : t("sources.import.segmentationHint")}
      errors={invalid ? <p className="app-error-text">{t("sources.import.segmentationInvalid")}</p> : undefined}
      actions={TEMPLATE_NAMES.map((name) => (
        <AppButton key={name} size="xs" variant="secondary" onClick={() => { onChange(segmentationTemplateText(name)); }}>
          {t(`sources.import.templates.${name}`)}
        </AppButton>
      ))}
    >
      {(control) => (
        <textarea {...control} className={`${FORM_CONTROL_CLASS} font-mono text-xs`} rows={8} value={text} spellCheck={false} aria-invalid={invalid}
          onChange={(event) => { onChange(event.target.value); }} />
      )}
    </Field>
  );
}
